import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, db, googleProvider } from "../firebase/config";
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  linkWithPhoneNumber,
  PhoneAuthProvider
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Google Sign In
  async function signInWithGoogle() {
    const result = await signInWithPopup(auth, googleProvider);
    await createUserProfile(result.user);
    return result;
  }

  // Email Sign Up
  async function signUp(email, password, name) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName: name });
    await createUserProfile(result.user, { displayName: name });
    return result;
  }

  // Email Sign In
  async function signIn(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Sign Out
  async function logout() {
    setUserProfile(null);
    return signOut(auth);
  }

  // Create user profile in Firestore
  async function createUserProfile(user, extraData = {}) {
    const userRef = doc(db, "users", user.uid);
    const snapshot = await getDoc(userRef);

    if (!snapshot.exists()) {
      // Send welcome email
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:4000"}/api/emails/welcome`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: user.email,
            name: user.displayName || extraData.displayName || "Valued Customer"
          })
        });
      } catch (e) { console.log("Welcome email failed:", e); }

      await setDoc(userRef, {
        uid: user.uid,
        name: user.displayName || extraData.displayName || "",
        email: user.email,
        phone: "",
        photoURL: user.photoURL || "",
        addresses: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    await loadUserProfile(user.uid);
  }

  // Load user profile from Firestore
  async function loadUserProfile(uid) {
    const userRef = doc(db, "users", uid);
    const snapshot = await getDoc(userRef);
    if (snapshot.exists()) {
      setUserProfile(snapshot.data());
    }
  }

  // Update user profile
  async function updateUserProfile(data) {
    if (!currentUser) return;
    const userRef = doc(db, "users", currentUser.uid);
    await setDoc(userRef, { ...data, updatedAt: serverTimestamp() }, { merge: true });
    await loadUserProfile(currentUser.uid);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await loadUserProfile(user.uid);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // ── Phone OTP ──
  function setupRecaptcha(containerId) {
    // Clear any existing verifier
    if (window.recaptchaVerifier) {
      try { window.recaptchaVerifier.clear(); } catch (e) {}
      window.recaptchaVerifier = null;
    }
    // Reset the container's inner HTML to remove any stale reCAPTCHA
    var container = document.getElementById(containerId);
    if (container) container.innerHTML = "";
    window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
      size: "invisible",
    });
    return window.recaptchaVerifier;
  }

  // Send OTP for phone login. phoneNumber must be E.164 format e.g. +918126484620
  async function sendPhoneOTP(phoneNumber, containerId) {
    const verifier = setupRecaptcha(containerId);
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, verifier);
    return confirmationResult;
  }

  // Verify OTP code from a confirmationResult
  async function verifyPhoneOTP(confirmationResult, code) {
    const result = await confirmationResult.confirm(code);
    return result.user;
  }

  // Send OTP to verify/link a phone number to the CURRENT logged-in user
  async function sendPhoneVerification(phoneNumber, containerId) {
    const verifier = setupRecaptcha(containerId);
    const confirmationResult = await linkWithPhoneNumber(auth.currentUser, phoneNumber, verifier);
    return confirmationResult;
  }

  const value = {
    currentUser,
    userProfile,
    signInWithGoogle,
    signUp,
    signIn,
    logout,
    updateUserProfile,
    loadUserProfile,
    sendPhoneOTP,
    verifyPhoneOTP,
    sendPhoneVerification
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
